import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { track } from '@/api/tracking'
import { VoiceDemoModalTrigger } from '@/features/demos/VoiceDemoModalTrigger'
import {
    InvoicesCard,
    RecommendationCard,
    ResultCard,
    SuppliersCard,
    WhatsAppCard,
} from '@/features/router-landing/RouterMocks'
import { SECTION_VIEW_DWELL_MS, useSectionViewTrack } from '@/hooks/useSectionViewTrack'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { cn } from '@/lib/utils'

import { TipiCaptureCard, TipiPriceAlertCard } from './WhyTipiMocks'
import { FOOD_COST_LANDING_PAGE } from './constants'

const STEPS = ['capture', 'order', 'act', 'see'] as const
type Step = (typeof STEPS)[number]

function StepScene({ step }: { step: Step }) {
    const { t } = useTranslation()

    if (step === 'capture') return <TipiCaptureCard />
    if (step === 'order') {
        return (
            <>
                <InvoicesCard compact />
                <WhatsAppCard
                    compact
                    message={t(
                        'foodCost.howItWorks.flow.order.supplierMessage',
                        'Hola, revisando vuestra factura F-26/0620 vemos 278,30 € más que en los albaranes. ¿Nos indicáis a qué corresponde?',
                    )}
                />
            </>
        )
    }
    if (step === 'act') {
        return (
            <>
                <TipiPriceAlertCard className='bottom-auto left-0 right-auto top-0 -translate-y-2 -rotate-[4deg]' />
                <RecommendationCard compact className='translate-y-3' />
            </>
        )
    }
    return (
        <>
            <ResultCard compact className='z-10 -translate-y-3 pb-4' />
            <SuppliersCard compact className='translate-x-3 translate-y-2' />
        </>
    )
}

/**
 * "¿Cómo funciona Tipi?": capture → order → act → see, told with scroll. The card scene stays sticky
 * (top on mobile, right on desktop) and swaps as each step reaches the middle of the screen.
 */
export function HowItWorksFlowSection() {
    const { t } = useTranslation()
    const sectionRef = useRef<HTMLElement>(null)
    const stepRefs = useRef<(HTMLLIElement | null)[]>([])
    const trackedSteps = useRef(new Set<Step>())
    const [observedIndex, setObservedIndex] = useState<number | null>(null)
    const activeIndex = observedIndex ?? 0
    const isDesktop = useMediaQuery('(min-width: 768px)')

    useSectionViewTrack(sectionRef, 'How It Works', { landing_page: FOOD_COST_LANDING_PAGE })

    useEffect(() => {
        // A one-pixel line across the viewport: the step crossing it is the active one. On mobile the
        // scene is sticky at the top, so the line sits lower to switch once the step text is visible below it.
        const linePct = isDesktop ? 50 : 70
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return
                    const index = Number((entry.target as HTMLElement).dataset.index)
                    if (Number.isInteger(index)) setObservedIndex(index)
                })
            },
            { rootMargin: `-${linePct}% 0px -${100 - linePct}% 0px` },
        )
        stepRefs.current.forEach(step => step && observer.observe(step))
        return () => observer.disconnect()
    }, [isDesktop])

    useEffect(() => {
        if (observedIndex === null) return
        const step = STEPS[observedIndex]
        if (trackedSteps.current.has(step)) return
        const timer = window.setTimeout(() => {
            trackedSteps.current.add(step)
            track('How It Works Step Viewed', { landing_page: FOOD_COST_LANDING_PAGE, step })
        }, SECTION_VIEW_DWELL_MS)
        return () => window.clearTimeout(timer)
    }, [observedIndex])

    return (
        <section
            ref={sectionRef}
            className='bg-[#17130E] pt-12 [background-image:radial-gradient(ellipse_130%_100%_at_50%_0%,rgba(244,80,42,0.22)_0%,rgba(244,80,42,0.05)_45%,transparent_70%)] [background-repeat:no-repeat] [background-size:100%_240px] md:pt-20 md:[background-image:radial-gradient(ellipse_70%_60%_at_50%_0%,rgba(244,80,42,0.18)_0%,rgba(244,80,42,0.04)_45%,transparent_70%)] md:[background-size:auto]'
            aria-labelledby='how-it-works-heading'
        >
            <div className='mx-auto max-w-[1180px] px-[22px] md:px-10'>
                <div className='flex flex-col gap-2.5'>
                    <h2
                        id='how-it-works-heading'
                        className='m-0 text-pretty text-[28px] font-medium leading-[1.15] tracking-[-0.8px] text-[#F4502A] md:text-[42px]'
                    >
                        {t('foodCost.howItWorks.title')}
                    </h2>
                    <p className='m-0 max-w-[56ch] text-[15px] leading-[1.65] text-[#B8AFA3] md:text-[17px]'>
                        {t('foodCost.howItWorks.answerCapsule')}
                    </p>
                </div>

                <div className='relative mt-4 md:mt-0 md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-12'>
                    <div className='sticky top-[72px] z-10 -mx-[22px] bg-[#17130E] px-[22px] pb-2 pt-3 md:top-[calc(50vh-200px)] md:order-2 md:mx-0 md:h-[400px] md:self-start md:bg-transparent md:px-0 md:pt-0'>
                        <div
                            aria-hidden='true'
                            className='pointer-events-none relative h-[236px] font-archivo text-tipi-fg md:h-[360px]'
                        >
                            <div className='absolute left-1/2 top-0 h-[260px] w-[340px] origin-top -translate-x-1/2 scale-[0.88] md:top-1/2 md:origin-center md:-translate-y-1/2 md:scale-[1.22]'>
                                {STEPS.map((step, index) => (
                                    <div
                                        key={step}
                                        className={cn(
                                            'absolute inset-0 transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none',
                                            index === activeIndex ? 'opacity-100' : 'translate-y-4 opacity-0',
                                        )}
                                    >
                                        <StepScene step={step} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className='mt-1 flex justify-center gap-1.5' aria-hidden='true'>
                            {STEPS.map((step, index) => (
                                <span
                                    key={step}
                                    className={cn(
                                        'h-1.5 rounded-full transition-all duration-300',
                                        index === activeIndex ? 'w-6 bg-[#F4502A]' : 'w-1.5 bg-[#F5EEE4]/25',
                                    )}
                                />
                            ))}
                        </div>
                        <div
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-x-0 -bottom-6 h-6 bg-gradient-to-b from-[#17130E] to-transparent md:hidden'
                        />
                    </div>

                    <ol className='m-0 list-none p-0 pb-[8vh] md:order-1 md:pb-[20vh]'>
                        {STEPS.map((step, index) => (
                            <li
                                key={step}
                                ref={element => {
                                    stepRefs.current[index] = element
                                }}
                                data-index={index}
                                className={cn(
                                    'flex min-h-[42vh] flex-col justify-center gap-2.5 py-6 transition-opacity duration-300 md:min-h-[72vh]',
                                    index === activeIndex ? 'opacity-100' : 'opacity-60',
                                )}
                            >
                                <span className='flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F4502A]'>
                                    <span className='tabular-nums text-[#F5EEE4]/50'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    {t(`foodCost.howItWorks.flow.${step}.eyebrow`)}
                                </span>
                                <h3 className='m-0 text-balance text-[22px] font-medium leading-[1.2] tracking-[-0.4px] text-[#F5EEE4] md:text-[32px] md:tracking-[-0.8px]'>
                                    {t(`foodCost.howItWorks.flow.${step}.title`)}
                                </h3>
                                <p className='m-0 max-w-[52ch] text-[15px] leading-[1.6] text-[#C9C0B3] md:text-[17px]'>
                                    {t(`foodCost.howItWorks.flow.${step}.body`)}
                                </p>
                                {step === 'capture' ? (
                                    <VoiceDemoModalTrigger landingPage={FOOD_COST_LANDING_PAGE} className='pt-1' />
                                ) : null}
                                <p className='m-0 max-w-[52ch] border-l-2 border-[#F5EEE4]/15 pl-3 text-[13px] leading-[1.5] text-[#A89F90] md:text-[14px]'>
                                    {t(`foodCost.howItWorks.flow.${step}.versus`)}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    )
}
